// src/pages/Terms/Terms.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Terms.css';

const sections = [
  {
    n: '01',
    title: 'Introduction & Acceptance of Terms',
    body: (
      <>
        <p>Welcome to TREXAR ("TREXAR", "we", "us", "our", or the "Platform").</p>
        <p>These Terms and Conditions ("Terms") govern your access to and use of:</p>
        <ul>
          <li>The TREXAR mobile application</li>
          <li>The TREXAR website</li>
          <li>All products, features, tools, and services offered by TREXAR (collectively, the "Services")</li>
        </ul>
        <p>By accessing, registering, or using TREXAR, you confirm that:</p>
        <ul>
          <li>You have read and understood these Terms</li>
          <li>You agree to be legally bound by them</li>
          <li>You understand the risks associated with investments on the Platform</li>
        </ul>
        <p className="terms-highlight">If you do not agree to these Terms, you must not use TREXAR.</p>
      </>
    ),
  },
  {
    n: '02',
    title: 'Definitions',
    body: (
      <ul>
        <li><strong>User</strong> — any person who accesses or uses TREXAR</li>
        <li><strong>Investor</strong> — a User who invests in assets on the Platform</li>
        <li><strong>Assets</strong> — investment opportunities listed on TREXAR</li>
        <li><strong>SPV</strong> — Special Purpose Vehicle</li>
        <li><strong>Secondary Market</strong> — the internal marketplace where eligible assets may be traded</li>
        <li><strong>Auto-Invest Wallet</strong> — pooled investment features, not savings</li>
        <li><strong>Business</strong> — the underlying venture or company associated with an asset</li>
      </ul>
    ),
  },
  {
    n: '03',
    title: 'Eligibility to Use TREXAR',
    body: (
      <>
        <p>To use TREXAR, you must:</p>
        <ol>
          <li>Be at least 18 years old</li>
          <li>Have the legal capacity to enter binding contracts</li>
          <li>Provide accurate, current, and complete information</li>
          <li>Comply with all applicable Nigerian laws and regulations</li>
          <li>Not be prohibited from using financial or investment platforms</li>
        </ol>
        <p>TREXAR reserves the right to refuse access or terminate accounts at its sole discretion.</p>
      </>
    ),
  },
  {
    n: '04',
    title: 'Nature of TREXAR',
    body: (
      <>
        <h4>TREXAR is not a bank</h4>
        <ul>
          <li>Is not a bank</li>
          <li>Is not a microfinance institution</li>
          <li>Does not accept deposits</li>
          <li>Does not offer fixed or guaranteed interest</li>
        </ul>
        <p>Funds placed on TREXAR are not protected by NDIC or any deposit insurance scheme.</p>
        <h4>TREXAR is an investment platform</h4>
        <p>TREXAR provides users with access to private investment opportunities, including but not limited to:</p>
        <ul>
          <li>Nigerian private businesses</li>
          <li>Growth and expansion rounds</li>
          <li>Late-stage ventures</li>
          <li>Private equity investments</li>
        </ul>
        <p>All investments are subject to risk.</p>
      </>
    ),
  },
  {
    n: '05',
    title: 'Account Registration & Security',
    body: (
      <>
        <h4>Account creation</h4>
        <p>You must create an account to access investment features. During registration, you agree to provide accurate information.</p>
        <h4>Account responsibility</h4>
        <p>You are solely responsible for:</p>
        <ul>
          <li>Safeguarding your login credentials</li>
          <li>All activities conducted under your account</li>
        </ul>
        <p>TREXAR will not be liable for unauthorized access caused by your negligence.</p>
      </>
    ),
  },
  {
    n: '06',
    title: 'KYC, AML & Compliance',
    body: (
      <>
        <p>TREXAR complies with applicable KYC (Know Your Customer) and AML (Anti-Money Laundering) regulations. We may require:</p>
        <ul>
          <li>Government-issued identification</li>
          <li>Proof of address</li>
          <li>Bank verification</li>
          <li>Additional documents as required by law</li>
        </ul>
        <p>Failure to comply may result in restricted access, account suspension, or termination.</p>
      </>
    ),
  },
  {
    n: '07',
    title: 'Investment Structure & SPVs',
    body: (
      <>
        <h4>SPV-based investments</h4>
        <p>Most assets on TREXAR are structured using Special Purpose Vehicles (SPVs). When you invest:</p>
        <ul>
          <li>You invest in an SPV</li>
          <li>The SPV holds an interest in the underlying business</li>
          <li>You do NOT directly own the business</li>
        </ul>
        <h4>Asset information</h4>
        <p>All asset data, including valuations, projected returns, and financial metrics, are estimates provided for informational purposes only.</p>
      </>
    ),
  },
  {
    n: '08',
    title: 'Risk Disclosure',
    body: (
      <>
        <p>By using TREXAR, you expressly acknowledge and accept that:</p>
        <ol>
          <li>Investments may be illiquid</li>
          <li>Capital is at risk</li>
          <li>Returns are not guaranteed</li>
          <li>Business failures may occur</li>
          <li>Market, regulatory, and operational risks exist</li>
          <li>Past performance does not predict future results</li>
        </ol>
        <p className="terms-highlight">You are solely responsible for evaluating investment suitability.</p>
      </>
    ),
  },
  {
    n: '09',
    title: 'Asset Categories & Risk Levels',
    body: (
      <>
        <p>TREXAR may categorize assets into groups such as:</p>
        <ul>
          <li>Core Nigerian Assets</li>
          <li>Growth Rounds</li>
          <li>Scale-Up Ventures</li>
          <li>Private Equity</li>
        </ul>
        <p>Each category carries different risk levels. Higher potential returns generally come with higher risk.</p>
      </>
    ),
  },
  {
    n: '10',
    title: 'Secondary Market & Trading',
    body: (
      <>
        <h4>Availability</h4>
        <p>Only assets explicitly displayed on the core ng assets may be traded on the Secondary Market.</p>
        <h4>Pricing</h4>
        <p>Prices are determined by supply and demand, asset performance, and market sentiment. TREXAR does not guarantee liquidity or pricing.</p>
        <h4>Platform control</h4>
        <p>TREXAR reserves the right to set trading windows, suspend trading, delist assets, or cancel/reverse trades in exceptional circumstances.</p>
      </>
    ),
  },
  {
    n: '11',
    title: 'Auto-Invest Wallet',
    body: (
      <>
        <h4>Nature of feature</h4>
        <p>Funds placed in Auto-Invest or Investment Wallets are NOT savings, are NOT deposits, and are deployed into selected investments.</p>
        <h4>Risk</h4>
        <p>Returns are variable and dependent on asset performance. Capital is at risk.</p>
      </>
    ),
  },
  {
    n: '12',
    title: 'Fees & Charges',
    body: (
      <>
        <p>TREXAR may charge:</p>
        <ul>
          <li>Management fees</li>
          <li>Performance fees</li>
          <li>Trading fees</li>
          <li>Transaction or processing fees</li>
        </ul>
        <p>All fees will be disclosed prior to execution.</p>
      </>
    ),
  },
  {
    n: '13',
    title: 'Payouts & Withdrawals',
    body: (
      <>
        <h4>Profit distribution</h4>
        <p>Payouts depend on business performance, SPV agreements, and cash flow availability.</p>
        <h4>Delays</h4>
        <p>Delays may occur due to audits, regulatory checks, or business conditions. TREXAR is not liable for delays beyond its control.</p>
      </>
    ),
  },
  {
    n: '14',
    title: 'User Conduct & Prohibited Activities',
    body: (
      <>
        <p>You agree NOT to:</p>
        <ul>
          <li>Manipulate markets</li>
          <li>Spread false information</li>
          <li>Engage in fraud</li>
          <li>Use the Platform for illegal purposes</li>
        </ul>
        <p>Violations may lead to immediate termination.</p>
      </>
    ),
  },
  {
    n: '15',
    title: 'Intellectual Property',
    body: <p>All content, trademarks, software, and branding belong to TREXAR and may not be used without permission.</p>,
  },
  {
    n: '16',
    title: 'Disclaimer of Warranties',
    body: (
      <>
        <p>TREXAR is provided "as is" and "as available." We make no warranties regarding:</p>
        <ul>
          <li>Performance</li>
          <li>Accuracy</li>
          <li>Availability</li>
          <li>Profitability</li>
        </ul>
      </>
    ),
  },
  {
    n: '17',
    title: 'Limitation of Liability',
    body: (
      <>
        <p>To the fullest extent permitted by law, TREXAR shall not be liable for:</p>
        <ul>
          <li>Investment losses</li>
          <li>Business failures</li>
          <li>Market fluctuations</li>
          <li>Indirect or consequential damages</li>
        </ul>
      </>
    ),
  },
  {
    n: '18',
    title: 'Indemnification',
    body: (
      <>
        <p>You agree to indemnify and hold harmless TREXAR, its directors, officers, employees, and partners from claims arising from:</p>
        <ul>
          <li>Your use of the Platform</li>
          <li>Breach of these Terms</li>
          <li>Violation of laws</li>
        </ul>
      </>
    ),
  },
  {
    n: '19',
    title: 'Termination',
    body: (
      <>
        <p>TREXAR may suspend or terminate your account:</p>
        <ul>
          <li>For regulatory reasons</li>
          <li>For breach of these Terms</li>
          <li>For risk management</li>
        </ul>
        <p>Termination does not affect existing obligations.</p>
      </>
    ),
  },
  {
    n: '20',
    title: 'Data Protection & Privacy',
    body: (
      <p>TREXAR processes personal data in accordance with the Nigerian Data Protection Act and our Privacy Policy.</p>
    ),
  },
  {
    n: '21',
    title: 'Amendments',
    body: <p>TREXAR may modify these Terms at any time. Continued use constitutes acceptance.</p>,
  },
  {
    n: '22',
    title: 'Governing Law & Jurisdiction',
    body: <p>These Terms are governed by the laws of the Federal Republic of Nigeria. Disputes shall be resolved in Nigerian courts.</p>,
  },
  {
    n: '23',
    title: 'Anti-Fraud, Referral & Multi-Account Policy',
    body: (
      <>
        <h4>Single account policy</h4>
        <p>Each user is permitted to operate only one TREXAR account. Creating, controlling, or operating multiple accounts, directly or indirectly, is strictly prohibited, including:</p>
        <ul>
          <li>Using different email addresses, phone numbers, or identities</li>
          <li>Creating accounts on behalf of others for personal benefit</li>
          <li>Using automated systems or scripts to generate accounts</li>
        </ul>
        <p>TREXAR reserves the right to determine, at its sole discretion, whether multiple accounts are linked to a single individual or entity.</p>

        <h4>Referral program integrity</h4>
        <p>Users must not:</p>
        <ul>
          <li>Create fake or duplicate accounts to earn referral rewards</li>
          <li>Refer themselves, directly or indirectly</li>
          <li>Use misleading, deceptive, or spam methods to gain referrals</li>
          <li>Manipulate or exploit the referral system in any form</li>
        </ul>

        <h4>Prohibited activities</h4>
        <ul>
          <li>Self-referrals using multiple accounts</li>
          <li>Coordinated referral abuse (group farming, bot activity)</li>
          <li>Use of stolen, fake, or unverifiable identities</li>
          <li>Exploiting system loopholes for financial gain</li>
        </ul>

        <h4>Enforcement & penalties</h4>
        <p>Violations may result in, without prior notice:</p>
        <ul>
          <li>Immediate account suspension or permanent freeze</li>
          <li>Forfeiture of all referral rewards and associated earnings</li>
          <li>Reversal of any benefits gained through abusive activity</li>
          <li>Restriction of withdrawals</li>
          <li>Termination of access to the Platform</li>
        </ul>
        <p>TREXAR's decision in such matters shall be final.</p>

        <h4>Investigation rights</h4>
        <p>TREXAR may conduct internal investigations, request identity verification or supporting documentation, and use automated systems and manual review to detect abuse. Failure to comply with verification requests may result in account restriction.</p>

        <h4>No liability</h4>
        <p>TREXAR shall not be liable for any loss of funds, rewards, or access resulting from enforcement of this policy.</p>

        <h4>Amendments</h4>
        <p>TREXAR reserves the right to modify or update this policy at any time. Continued use of the Platform constitutes acceptance of any changes.</p>
      </>
    ),
  },
];

const Terms = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(0);

  const toggleSection = (idx) => {
    setOpenIndex((prev) => (prev === idx ? -1 : idx));
  };

  return (
    <div className="terms-page-wrapper">
      <div className="terms-page">
      <div className="terms-page-header">
        <button
          type="button"
          className="terms-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="terms-page-heading">
          <h1>Terms &amp; Conditions</h1>
          <p>Please review how TREXAR's services work</p>
        </div>
      </div>

      <div className="terms-meta-strip">
        <div className="terms-meta-chip">
          <span className="terms-meta-label">Effective</span>
          <span className="terms-meta-value">01 Jan 2025</span>
        </div>
        <div className="terms-meta-chip">
          <span className="terms-meta-label">Last updated</span>
          <span className="terms-meta-value">01 Jan 2026</span>
        </div>
      </div>

      <div className="terms-sections">
        {sections.map((section, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div key={section.n} className={`terms-card ${isOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className="terms-card-header"
                onClick={() => toggleSection(idx)}
                aria-expanded={isOpen}
              >
                <span className="terms-card-number">{section.n}</span>
                <span className="terms-card-title">{section.title}</span>
                <svg
                  className="terms-card-chevron"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {isOpen && <div className="terms-card-body">{section.body}</div>}
            </div>
          );
        })}
      </div>

      <a href="mailto:support@trexarng.com" className="terms-contact-card">
        <div className="terms-contact-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16v16H4z" opacity="0" />
            <path d="M22 6l-10 7L2 6" />
            <rect x="2" y="4" width="20" height="16" rx="2" />
          </svg>
        </div>
        <div>
          <p className="terms-contact-label">Questions about these Terms?</p>
          <span className="terms-contact-email">Contact support</span>
        </div>
        <svg className="terms-contact-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </a>
      </div>
    </div>
  );
};

export default Terms;